import { GuidanceSessionType } from '@fruitful/types';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar } from 'primereact/calendar';
import { useEffect, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    LabelRegular,
    Picker,
    ScrollView,
    useToastController,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { type MembershipDetails } from '../../entities/entityTypes';
import { getMembershipDetailsQueryKey } from '../../entities/membership/MembershipDetailsEntityProvider';
import { captureException } from '../../logger/Logger';
import { useCreateGuidanceSession } from '../hooks/useCreateGuidanceSession';
import { getMembershipGuidanceSessionDisplayName } from '../utils/membershipGuidanceSessionDisplayName';

interface CreateGuidanceSessionModalProps {
    isOpen: boolean;
    onClose: () => void;
    membershipDetails: MembershipDetails;
}

export const CreateGuidanceSessionModal = ({
    isOpen,
    onClose,
    membershipDetails,
}: CreateGuidanceSessionModalProps) => {
    const toast = useToastController();
    const queryClient = useQueryClient();
    const { mutateAsync: createGuidanceSession } = useCreateGuidanceSession();

    const assignedGuideId = membershipDetails.guideId ?? '';

    const SESSION_TYPE_OPTIONS = Object.values(GuidanceSessionType).map(
        (type) => ({
            label: getMembershipGuidanceSessionDisplayName(
                type,
                membershipDetails,
            ),
            value: type,
        }),
    );

    const [sessionType, setSessionType] = useState<GuidanceSessionType | null>(
        null,
    );
    const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(
        new Date(),
    );
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSessionType(null);
        setSelectedDateTime(new Date());
        setIsSubmitting(false);
    }, [isOpen]);

    const missingGuide = assignedGuideId === '';
    const isFutureStart =
        selectedDateTime != null && selectedDateTime.getTime() > Date.now();

    const canSubmit =
        sessionType != null &&
        !missingGuide &&
        selectedDateTime != null &&
        !isFutureStart &&
        !isSubmitting;

    const handleCreate = async () => {
        if (sessionType == null || selectedDateTime == null || !canSubmit) {
            return;
        }

        setIsSubmitting(true);

        try {
            await createGuidanceSession({
                body: {
                    membershipId: membershipDetails.id,
                    sessionType,
                    startTimeUtc: selectedDateTime.toISOString(),
                },
            });

            await queryClient.invalidateQueries({
                queryKey: [
                    getMembershipDetailsQueryKey({
                        membershipId: membershipDetails.id,
                    }),
                ],
                exact: true,
            });

            onClose();

            toast.show('Session added.', MutationSuccessToastOptions);
        } catch (error) {
            toast.show(
                'Unable to create guidance session',
                MutationErrorToastOptions(error),
            );
            captureException(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GroveSimpleModal isOpen={isOpen} onClose={onClose} hasCloseButton>
            <ScrollView maxHeight="80vh">
                <YStack gap={16} padding={16} width={520}>
                    <BodyLargeEmphasized>Add past session</BodyLargeEmphasized>
                    <BodyMediumRegular>
                        Adds a past session record for this membership.
                    </BodyMediumRegular>

                    {missingGuide ? (
                        <BodyMediumRegular>
                            Assign a guide to this membership before creating a
                            session.
                        </BodyMediumRegular>
                    ) : (
                        <>
                            <YStack gap={8}>
                                <LabelRegular color="$gray1000">
                                    Session type
                                </LabelRegular>
                                <Picker
                                    size="regular"
                                    value={sessionType}
                                    placeholder="Select type"
                                    options={SESSION_TYPE_OPTIONS}
                                    disabled={isSubmitting}
                                    onChange={setSessionType}
                                />
                            </YStack>

                            <YStack gap={8}>
                                <LabelRegular color="$gray1000">
                                    Date and time
                                </LabelRegular>
                                <Calendar
                                    id="session-datetime"
                                    value={selectedDateTime}
                                    onChange={(event) => {
                                        const value = event.value;
                                        setSelectedDateTime(
                                            value instanceof Date
                                                ? value
                                                : null,
                                        );
                                    }}
                                    inline
                                    showTime
                                    hourFormat="12"
                                    showButtonBar
                                    dateFormat="mm/dd/yy"
                                    maxDate={new Date()}
                                    disabled={isSubmitting}
                                />
                            </YStack>

                            <Button
                                variant={ButtonVariant.PRIMARY}
                                size={ButtonSize.SMALL}
                                text={
                                    isSubmitting
                                        ? 'Adding...'
                                        : 'Add past session'
                                }
                                disabled={!canSubmit}
                                onPress={() => {
                                    void handleCreate();
                                }}
                            />
                        </>
                    )}
                </YStack>
            </ScrollView>
        </GroveSimpleModal>
    );
};
